// src/utils/useAuthFetch.ts
// What? A custom hook for making authenticated API requests.
// Why? To handle different authentication methods (Auth0, EntraID, and unauthenticated).
// How?
// - Uses the useAuth0 hook for Auth0 authentication.
// - Uses the useMsal hook for EntraID authentication.
// - Provides a default implementation for unauthenticated requests.
// - Supports different response types (JSON or raw response).
// - Logs errors and displays them to the user.
import { useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useMsal } from "@azure/msal-react";
import {
  AccountInfo,
  InteractionRequiredAuthError,
  InteractionStatus,
} from "@azure/msal-browser";
import { useUnsafeAuthProvider } from "../components/UnsafeAuthProvider";
import { useErrorMessageProvider } from "../components/ErrorMessageProvider";
import { getBrowserTimezone } from "./userTimezone";
import { MultipartUploadTimeouts, uploadMultipart } from "./multipartUpload";
import {
  getApiBaseUrl,
  getAuthenticationMethod,
  isMockApiRuntime,
} from "../config/runtimeConfig";

/**
 * Per-call overrides for how a failure is surfaced.
 *
 * suppressNetworkBanner is for background calls the user never initiated —
 * page-load hydration and the like. The connectivity banner is global and
 * deliberately not tag-scoped, so without an opt-out one blocked endpoint
 * tells the user their whole connection is down. Suppressing it here loses
 * nothing during a real outage: every user-initiated call still raises it.
 */
export type AuthFetchBehavior = {
  suppress401Toast?: boolean;
  suppressNetworkBanner?: boolean;
  multipartUpload?: MultipartUploadTimeouts;
};

type AuthFetchFn = (
  endpoint: string,
  tag: string,
  options?: RequestInit,
  timeout?: number,
  behavior?: AuthFetchBehavior,
) => Promise<any>;

const ENTRA_SCOPES = [
  "api://0501f640-94a0-4fd9-a7ae-0e8fa5938130/access_as_user",
  "email",
  "openid",
  "profile",
];

const NETWORK_OR_CORS_MESSAGE =
  "We’re having trouble reaching X-Machine. Check your connection and try again.";
const NETWORK_OR_CORS_DEDUPE_MS = 30000;

let auth0ForcedRefreshPromise: Promise<string> | null = null;
let entraForcedRefreshPromise: Promise<string> | null = null;
let authRedirectStarted = false;
let lastNetworkOrCorsBannerAt = 0;

type AuthFetchErrorCode = "auth_interaction_required" | "network_or_cors";

/**
 * Turn a server error body into something a person can read.
 *
 * THE SERVER SENDS A PERFECTLY GOOD REASON AND THE CLIENT THROWS IT AWAY. Twice
 * in two days this product showed a user a raw object or a parser exception in
 * place of the explanation the API had already provided: "Failed to create
 * canvas: [object Object]" on a validation failure, and a JSON parser message
 * quoting the user's own CSV on export. Those are not two coincidences, they are
 * one client defect with two witnesses, so this is fixed at the boundary rather
 * than at each site as somebody trips over it.
 *
 * The specific trap is FastAPI's validation shape. For most errors `detail` is a
 * string, so a consumer that interpolates it reads correctly and nobody notices.
 * ON A 422 `detail` IS AN ARRAY OF OBJECTS, and the same interpolation yields
 * "[object Object]" -- the failure appears only on the errors that carry the most
 * information, which is exactly backwards.
 *
 * Returns undefined when the body carries nothing usable, so the caller keeps its
 * status-based message rather than replacing a real status with an empty string.
 */
export function describeErrorBody(
  body: Record<string, unknown> | undefined,
): string | undefined {
  if (!body) return undefined;
  const detail = body.detail;

  if (typeof detail === "string" && detail.trim()) return detail;

  // FastAPI validation: [{ loc: ["body","name"], msg: "...", type: "..." }, ...]
  if (Array.isArray(detail)) {
    const parts = detail
      .map((d) => {
        if (typeof d === "string") return d;
        if (!d || typeof d !== "object") return null;
        const item = d as { loc?: unknown; msg?: unknown };
        const msg = typeof item.msg === "string" ? item.msg : null;
        if (!msg) return null;
        // Drop the leading "body"/"query" segment; it names the transport, not
        // the field, and the reader already knows they submitted a form.
        const loc = Array.isArray(item.loc)
          ? item.loc
              .filter(
                (p) => typeof p === "string" && p !== "body" && p !== "query",
              )
              .join(".")
          : "";
        return loc ? `${loc}: ${msg}` : msg;
      })
      .filter((p): p is string => Boolean(p));
    // RETURN HERE EVEN WHEN EMPTY. An array is also an object, so falling
    // through would hand it to the JSON.stringify branch below and render
    // '[{"nope":1}]' -- which is [object Object] with extra steps and no more
    // meaning. If a validation array carries no readable message, we have
    // nothing to say and the caller's status message is the better answer.
    return parts.length ? parts.join("; ") : undefined;
  }

  if (detail && typeof detail === "object") {
    const structured = detail as { message?: unknown; findings?: unknown };
    if (Array.isArray(structured.findings)) {
      const findings = structured.findings
        .map((finding) => {
          if (!finding || typeof finding !== "object") return null;
          const item = finding as { code?: unknown; message?: unknown };
          if (typeof item.message !== "string" || !item.message.trim()) {
            return null;
          }
          return typeof item.code === "string" && item.code.trim()
            ? `${item.code}: ${item.message}`
            : item.message;
        })
        .filter((finding): finding is string => Boolean(finding));
      if (findings.length) return findings.join("; ");
    }
    if (typeof structured.message === "string" && structured.message.trim()) {
      return structured.message;
    }
  }

  // A non-empty object with no recognised shape is still better rendered as its
  // fields than as "[object Object]".
  if (detail && typeof detail === "object") {
    try {
      const s = JSON.stringify(detail);
      if (s && s !== "{}") return s;
    } catch {
      /* fall through to undefined */
    }
  }

  if (typeof body.message === "string" && body.message.trim()) {
    return body.message;
  }
  return undefined;
}

class AuthFetchError extends Error {
  code?: AuthFetchErrorCode;
  response?: Record<string, unknown>;
  status?: number;
  suppressToast?: boolean;

  constructor(
    message: string,
    options: {
      code?: AuthFetchErrorCode;
      response?: Record<string, unknown>;
      status?: number;
      suppressToast?: boolean;
    } = {},
  ) {
    super(message);
    this.name = "AuthFetchError";
    this.code = options.code;
    this.response = options.response;
    this.status = options.status;
    this.suppressToast = options.suppressToast;
  }
}

export const __resetAuthFetchGuardsForTests = () => {
  auth0ForcedRefreshPromise = null;
  entraForcedRefreshPromise = null;
  authRedirectStarted = false;
  lastNetworkOrCorsBannerAt = 0;
};

const isAbortError = (error: unknown): boolean =>
  error instanceof DOMException && error.name === "AbortError";

/**
 * True only for an abort the CALLER asked for.
 *
 * The internal timeout below aborts with a TimeoutError, which stays a real
 * failure; a caller's cancellation is not one, so it must not be retried,
 * toasted, or counted as a connectivity problem.
 */
const isCallerAbort = (
  signal: AbortSignal | null | undefined,
  error: unknown,
): boolean =>
  !!signal?.aborted && (isAbortError(error) || error === signal.reason);

const isNetworkOrCorsError = (error: unknown): boolean => {
  if (error instanceof AuthFetchError) {
    return error.code === "network_or_cors";
  }
  return error instanceof TypeError && error.message === "Failed to fetch";
};

const toNetworkOrCorsError = (error: unknown): AuthFetchError => {
  if (error instanceof AuthFetchError && error.code === "network_or_cors") {
    return error;
  }
  return new AuthFetchError(
    error instanceof Error ? error.message : "Failed to fetch",
    {
      code: "network_or_cors",
    },
  );
};

const isInteractionRequiredError = (error: unknown): boolean => {
  if (error instanceof InteractionRequiredAuthError) return true;
  if (!(error instanceof Error)) return false;
  const maybeAuthError = error as Error & {
    error?: string;
    errorCode?: string;
  };
  return (
    error.name === "InteractionRequiredAuthError" ||
    maybeAuthError.error === "login_required" ||
    maybeAuthError.error === "consent_required" ||
    maybeAuthError.error === "interaction_required" ||
    maybeAuthError.errorCode === "interaction_required"
  );
};

const authInteractionRequiredError = () =>
  new AuthFetchError("Authentication interaction is required.", {
    code: "auth_interaction_required",
    suppressToast: true,
  });

const isBillingNotConfigured = (error: any): boolean => {
  const detail = error?.response?.detail;
  return (
    detail !== null &&
    typeof detail === "object" &&
    detail.code === "billing_not_configured"
  );
};

export const shouldShowRoutineToast = (
  tag: string,
  error: any,
  isAbort: boolean,
) =>
  !tag.includes("fetch") &&
  !tag.includes("BKG") &&
  !isAbort &&
  !isBillingNotConfigured(error) &&
  error.status !== 409 &&
  !error.suppressToast;

const addQueryParam = (
  endpoint: string,
  param: string,
  value: string,
): string => {
  const urlObj = new URL(endpoint, window.location.origin);
  if (!urlObj.searchParams.has(param)) {
    urlObj.searchParams.append(param, value);
  }
  return urlObj.pathname + urlObj.search;
};

const withBearerToken = (options: RequestInit, token: string): RequestInit => {
  const headers = new Headers(options.headers);
  headers.set("Authorization", `Bearer ${token}`);
  return { ...options, headers };
};

// What? A custom hook for making authenticated API requests.
// Why? To handle different authentication methods (Auth0, EntraID, and unauthenticated).
// How?
// - Uses the useAuth0 hook for Auth0 authentication.
// - Uses the useMsal hook for EntraID authentication.
// - Provides a default implementation for unauthenticated requests.
// - Supports different response types (JSON or raw response).
// - Logs errors and displays them to the user.
export const useAuthFetch = (response_type: string = "json") => {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const { instance, accounts, inProgress } = useMsal();
  const { unsafeUserName } = useUnsafeAuthProvider();
  const { registerError } = useErrorMessageProvider();

  const registerNetworkOrCorsError = useCallback(() => {
    const now = Date.now();
    if (now - lastNetworkOrCorsBannerAt < NETWORK_OR_CORS_DEDUPE_MS) {
      return;
    }
    lastNetworkOrCorsBannerAt = now;
    registerError(NETWORK_OR_CORS_MESSAGE);
  }, [registerError]);

  const handleFetchError = useCallback(
    (error: any, tag: string) => {
      if (error.suppressToast) {
        return;
      }
      const action_tag: string = tag === "" ? "API Error" : `Failed to ${tag}`;
      const isAbort =
        error.name === "AbortError" || error.name === "TimeoutError";
      const error_detail: string = isAbort
        ? "Server did not respond. Please try again."
        : describeErrorBody(error.response) || error.message || "Unknown error";

      // Only register errors for non-routine operations to reduce noise
      // Skip 409 Conflict -- handled by dedicated UI (conflict dialog, dependents warning)
      if (shouldShowRoutineToast(tag, error, isAbort)) {
        registerError(`${action_tag}: ${error_detail}`);
      }
      if (error.status !== 409) {
        console.error(error, `${action_tag}: ${error_detail}`);
      }
    },
    [registerError],
  );

  const defaultFetch = useCallback(
    async (
      endpoint: string,
      options: RequestInit = {},
      timeout = 60000,
      behavior: AuthFetchBehavior = {},
    ) => {
      const url = `${getApiBaseUrl()}${endpoint}`;
      const controller = new AbortController();
      const id = behavior.multipartUpload
        ? undefined
        : setTimeout(
            () =>
              controller.abort(
                new DOMException(
                  `Request timed out after ${timeout / 1000}s`,
                  "TimeoutError",
                ),
              ),
            timeout,
          );

      // The request carries this hook's own signal, so a caller's signal would
      // otherwise be dropped: passing it straight through loses the timeout, and
      // spreading options over it loses the caller's. Link the two instead, so
      // whichever fires first aborts the request with its own reason. React Query
      // hands every query function a signal and aborts it when a query is
      // superseded or its view unmounts, which is how a stale search stops.
      const callerSignal = options.signal ?? undefined;
      const abortFromCaller = () => controller.abort(callerSignal?.reason);
      if (callerSignal) {
        if (callerSignal.aborted) abortFromCaller();
        else callerSignal.addEventListener("abort", abortFromCaller);
      }

      const headers = new Headers(options.headers);
      headers.set("X-User-Timezone", getBrowserTimezone());

      try {
        const response = behavior.multipartUpload
          ? await uploadMultipart(
              url,
              { ...options, headers, signal: controller.signal },
              behavior.multipartUpload,
            )
          : await fetch(url, {
              ...options,
              headers,
              signal: controller.signal,
              mode: "cors",
              // Reviewed for #158: existing backend deployments are configured for
              // credentialed CORS. Keep include here; auth hardening happens via the
              // bearer-token retry/redirect path above, not by changing CORS mode.
              credentials: "include",
            });

        if (!response.ok) {
          // Only attach a parsed body when the server actually sent a usable one.
          // Fabricating { detail: "Unknown error" } for non-JSON/empty bodies (#3871)
          // hid the real status from every consumer that prefers response.detail over
          // the error message -- the user saw the literal string "Unknown error".
          // Leaving response undefined lets those consumers fall back to the message
          // below, which names the actual status.
          let errorData: Record<string, unknown> | undefined;
          const contentType = response.headers.get("content-type");
          if (contentType && contentType.includes("application/json")) {
            try {
              const parsed = await response.json();
              if (
                parsed &&
                typeof parsed === "object" &&
                Object.keys(parsed).length > 0
              ) {
                errorData = parsed as Record<string, unknown>;
              }
            } catch {
              errorData = undefined;
            }
          }

          // Attach additional properties to the Error object
          const error = new AuthFetchError(
            describeErrorBody(errorData) ??
              `HTTP Error: status: ${response.status} (${response.statusText})`,
            {
              response: errorData,
              status: response.status,
            },
          );

          throw error;
        }

        // Handle 204 No Content separately (don't try to parse response)
        if (response.status === 204) {
          return null;
        }

        if (response_type === "json") {
          return await response.json();
        }

        return response;
      } catch (error: any) {
        if (error instanceof TypeError && error.message === "Failed to fetch") {
          throw toNetworkOrCorsError(error);
        }
        throw error;
      } finally {
        if (id !== undefined) clearTimeout(id);
        callerSignal?.removeEventListener("abort", abortFromCaller);
      }
    },
    [response_type],
  );

  const requestWithNetworkRetry = useCallback(
    async (
      endpoint: string,
      tag: string,
      options: RequestInit = {},
      timeout = 60000,
      behavior: AuthFetchBehavior = {},
    ) => {
      // A network/CORS failure gives no signal about whether the request actually
      // reached the server, so replaying it can double-apply a mutation (e.g. a
      // create/import/delete whose response was lost). Only auto-retry idempotent
      // methods; fetch defaults to GET when no method is set.
      const method = (options.method ?? "GET").toUpperCase();
      const isRetryableMethod = method === "GET" || method === "HEAD";

      try {
        return await defaultFetch(endpoint, options, timeout, behavior);
      } catch (error) {
        if (isCallerAbort(options.signal, error)) throw error;
        if (!isNetworkOrCorsError(error)) {
          if ((error as any).status === 401 && behavior.suppress401Toast) {
            throw error;
          }
          handleFetchError(error, tag);
          throw error;
        }
        // Non-idempotent request that failed with a network/CORS error: do not
        // replay it. Surface the network error the same way the retry path would.
        if (!isRetryableMethod) {
          const typedError = toNetworkOrCorsError(error);
          if (!behavior.suppressNetworkBanner) {
            registerNetworkOrCorsError();
          }
          console.error(typedError, "Network or CORS error while reaching API");
          throw typedError;
        }
      }

      try {
        return await defaultFetch(endpoint, options, timeout, behavior);
      } catch (error) {
        if (isCallerAbort(options.signal, error)) throw error;
        if (isNetworkOrCorsError(error)) {
          const typedError = toNetworkOrCorsError(error);
          if (!behavior.suppressNetworkBanner) {
            registerNetworkOrCorsError();
          }
          console.error(typedError, "Network or CORS error while reaching API");
          throw typedError;
        }
        if ((error as any).status === 401 && behavior.suppress401Toast) {
          throw error;
        }
        handleFetchError(error, tag);
        throw error;
      }
    },
    [defaultFetch, handleFetchError, registerNetworkOrCorsError],
  );

  const unsafeFetch = useCallback(
    async (
      endpoint: string,
      tag: string,
      options: RequestInit = {},
      timeout = 60000,
      behavior: AuthFetchBehavior = {},
    ) => {
      endpoint = addQueryParam(endpoint, "user_name", unsafeUserName);
      return await requestWithNetworkRetry(
        endpoint,
        tag,
        options,
        timeout,
        behavior,
      );
    },
    [requestWithNetworkRetry, unsafeUserName],
  );

  const startAuth0Redirect = useCallback(async (): Promise<never> => {
    if (!authRedirectStarted) {
      authRedirectStarted = true;
      await loginWithRedirect({
        appState: {
          returnTo: window.location.pathname + window.location.search,
        },
      });
    }
    throw authInteractionRequiredError();
  }, [loginWithRedirect]);

  const getAuth0AccessToken = useCallback(
    async (forceRefresh: boolean): Promise<string> => {
      try {
        if (!forceRefresh) {
          return await getAccessTokenSilently();
        }
        if (!auth0ForcedRefreshPromise) {
          auth0ForcedRefreshPromise = Promise.resolve(
            getAccessTokenSilently({
              cacheMode: "off",
            } as any),
          )
            .then((token: any) =>
              typeof token === "string" ? token : token.access_token,
            )
            .finally(() => {
              auth0ForcedRefreshPromise = null;
            });
        }
        return await auth0ForcedRefreshPromise;
      } catch (error) {
        if (isInteractionRequiredError(error)) {
          return await startAuth0Redirect();
        }
        throw error;
      }
    },
    [getAccessTokenSilently, startAuth0Redirect],
  );

  const auth0Fetch = useCallback(
    async (
      endpoint: string,
      tag: string,
      options: RequestInit = {},
      timeout = 60000,
      behavior: AuthFetchBehavior = {},
    ) => {
      const token = await getAuth0AccessToken(false);
      const authedOptions = withBearerToken(options, token);

      try {
        return await requestWithNetworkRetry(
          endpoint,
          tag,
          authedOptions,
          timeout,
          { ...behavior, suppress401Toast: true },
        );
      } catch (error: any) {
        if (error.status !== 401) {
          throw error;
        }
      }

      const refreshedToken = await getAuth0AccessToken(true);
      return await requestWithNetworkRetry(
        endpoint,
        tag,
        withBearerToken(options, refreshedToken),
        timeout,
        behavior,
      );
    },
    [getAuth0AccessToken, requestWithNetworkRetry],
  );

  const activeEntraAccount = useCallback(
    (): AccountInfo | undefined =>
      instance.getActiveAccount?.() ?? accounts[0] ?? undefined,
    [accounts, instance],
  );

  const startEntraRedirect = useCallback(
    async (request: {
      scopes: string[];
      account?: AccountInfo;
    }): Promise<never> => {
      if (!authRedirectStarted && inProgress === InteractionStatus.None) {
        authRedirectStarted = true;
        await instance.loginRedirect(request);
      }
      throw authInteractionRequiredError();
    },
    [inProgress, instance],
  );

  const getEntraAccessToken = useCallback(
    async (forceRefresh: boolean): Promise<string> => {
      const request = {
        scopes: ENTRA_SCOPES,
        account: activeEntraAccount(),
        ...(forceRefresh ? { forceRefresh: true } : {}),
      };

      await instance.initialize();

      try {
        if (!forceRefresh) {
          const response = await instance.acquireTokenSilent(request);
          return response.accessToken;
        }
        if (!entraForcedRefreshPromise) {
          entraForcedRefreshPromise = instance
            .acquireTokenSilent(request)
            .then((response) => response.accessToken)
            .finally(() => {
              entraForcedRefreshPromise = null;
            });
        }
        return await entraForcedRefreshPromise;
      } catch (error) {
        if (isInteractionRequiredError(error)) {
          return await startEntraRedirect(request);
        }
        throw error;
      }
    },
    [activeEntraAccount, instance, startEntraRedirect],
  );

  const entraIDFetch = useCallback(
    async (
      endpoint: string,
      tag: string,
      options: RequestInit = {},
      timeout = 60000,
      behavior: AuthFetchBehavior = {},
    ) => {
      const token = await getEntraAccessToken(false);
      const authedOptions = withBearerToken(options, token);

      try {
        return await requestWithNetworkRetry(
          endpoint,
          tag,
          authedOptions,
          timeout,
          { ...behavior, suppress401Toast: true },
        );
      } catch (error: any) {
        if (error.status !== 401) {
          throw error;
        }
      }

      const refreshedToken = await getEntraAccessToken(true);
      return await requestWithNetworkRetry(
        endpoint,
        tag,
        withBearerToken(options, refreshedToken),
        timeout,
        behavior,
      );
    },
    [getEntraAccessToken, requestWithNetworkRetry],
  );

  return useCallback<AuthFetchFn>(
    async (endpoint, tag, options, timeout, behavior) => {
      if (isMockApiRuntime()) {
        return await unsafeFetch(endpoint, tag, options, timeout, behavior);
      }
      const authenticationMethod = getAuthenticationMethod();
      if (authenticationMethod === "AUTH0") {
        return await auth0Fetch(endpoint, tag, options, timeout, behavior);
      }
      if (authenticationMethod === "ENTRAID") {
        return await entraIDFetch(endpoint, tag, options, timeout, behavior);
      }
      return await unsafeFetch(endpoint, tag, options, timeout, behavior);
    },
    [auth0Fetch, entraIDFetch, unsafeFetch],
  );
};
